import React, { Component } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import qs from 'qs'

// configs and utils
import l, { isBp, isGs } from 'config/localization'
import isLoggedIn from 'utils/auth'
// import constants from 'config/constants'

// redux modules
import * as authModule from '../../redux/modules/authentication'
import * as placesModule from '../../redux/modules/places'

// import partials
import PayfastPaymentScript from './PayfastPaymentScript'
import Seo from './Seo'

// load ModalsCanvas, Navbar synchronously to avoid scroll bug
// where Y scroll position has to be > 0 to show modal
import ModalsCanvas from 'components/Modals/ModalsCanvas'
import Navbar from 'components/Navbar'

// import styles
import { Main, Wrapper } from './styles'

// Load components synchronously
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import Footer from 'components/Footer'
import Header from 'components/Header'

// keyboard key codes
const ESCAPE_KEYCODE = 27
const ONE_KEYCODE = 49
const TWO_KEYCODE = 50
const THREE_KEYCODE = 51
const FOUR_KEYCODE = 52
const FIVE_KEYCODE = 53
const SIX_KEYCODE = 54

const getTitle = ogTitle => {
  if (isGs) return 'Growsmart'
  return ogTitle || 'Brownie Points | Empowering Good Deeds'
}

const GrowsmartDowntimeMessage = () => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        fontSize: '15px'
      }}
    >
      <div>We are temporarily down for some maintenance.</div>

      <div style={{ marginTop: '10px' }}>We apologize for any inconvenience.</div>
    </div>
  )
}

const getSuccessClosedUrl = () => {
  return window.location.search
    .replace('hasRedirected=true', '')
    .replace('pending=true', '')
    .replace('success=true', '')
    .replace('teamInvite=true', '')
    .replace(/\&$/, '')
}

function withNavigation(Component) {
  return function NavigationWrappedComponent(props) {
    const navigate = useNavigate()
    return <Component {...props} navigate={navigate} />
  }
}

class PageWrapper extends Component {
  constructor(props) {
    super(props)
    this.state = {
      discoverDropdownVisible: false,
      pricingDropdownVisible: false,
      scrollY: 0,
      visibleModal: null
    }

    this.escFunction = this.escFunction.bind(this)
    this.closeAllModals = this.closeAllModals.bind(this)
    this.toggleMenuModal = this.toggleMenuModal.bind(this)
    this.toggleDiscoverDropdown = this.toggleDiscoverDropdown.bind(this)
    this.togglePricingDropdown = this.togglePricingDropdown.bind(this)
  }

  closeAllModals() {
    const urlSearch = getSuccessClosedUrl()
    const path = window.location.pathname + urlSearch
    this.props.navigate(path, { replace: true })

    this.setState({ visibleModal: null })

    if (typeof this.props.resetModalState === 'function') {
      this.props.resetModalState()
    }
  }

  toggleMenuModal(value) {
    const { visibleModal } = this.state
    const forceClose = value === 'close'
    this.setState({ visibleModal: visibleModal ? null : forceClose ? null : 'menu-modal' })

    const urlSearch = getSuccessClosedUrl()
    const path = window.location.pathname + urlSearch
    this.props.navigate(path, { replace: true })
  }

  togglePricingDropdown() {
    this.setState({ pricingDropdownVisible: !this.state.pricingDropdownVisible })
  }

  toggleDiscoverDropdown() {
    this.setState({ discoverDropdownVisible: !this.state.discoverDropdownVisible })
  }

  escFunction(event) {
    const loggedIn = isLoggedIn()

    // This feature is not for Growsmart
    if (!isBp) return true

    // determine if there's any open modal
    const anyModalIsOpen = !!this.state.visibleModal || !!this.props.visibleModal
    const menuModalIsOpen = this.state.visibleModal === 'menu-modal'

    // Any modal that is currently open will be closed
    if (anyModalIsOpen && event.keyCode === ESCAPE_KEYCODE) {
      // first close modals
      this.closeAllModals()

      // then scroll to previous Y position
      window.scrollTo({ top: this.state.scrollY })

      return true
    }

    // No modal is open so escape should open the menu modal
    if (event.keyCode === ESCAPE_KEYCODE) {
      this.setState({ scrollY: window.scrollY, visibleModal: 'menu-modal' })
    }

    if (menuModalIsOpen && event.keyCode === ONE_KEYCODE) {
      // Keyboard shortcut for key '1'
      this.setState({ visibleModal: null })
      this.props.navigate(loggedIn ? `/dashboard` : '/login')
    }

    if (menuModalIsOpen && loggedIn && event.keyCode === TWO_KEYCODE) {
      // Keyboard shortcut for key '2'
      this.setState({ visibleModal: null })

      const { userData } = this.props
      const profilePath = userData.username ? `/@${userData.username}` : `/users/${userData.id}`

      this.props.navigate(profilePath)
    }

    if (menuModalIsOpen && loggedIn && event.keyCode === THREE_KEYCODE) {
      // Keyboard shortcut for key '3'
      this.setState({ visibleModal: null })
      this.props.navigate(loggedIn ? `/dashboard/stories/new?eventType=GoodDeed` : `/stories`)
    }

    if (menuModalIsOpen && event.keyCode === FOUR_KEYCODE) {
      // Keyboard shortcut for key '4'
      this.setState({ visibleModal: null })
      this.props.navigate(`/search/${l('ROUTE_OPPS')}`)
    }

    if (menuModalIsOpen && event.keyCode === FIVE_KEYCODE) {
      // Keyboard shortcut for key '5'
      this.setState({ visibleModal: null })
      this.props.navigate(`/nonprofits`)
    }

    if (menuModalIsOpen && event.keyCode === SIX_KEYCODE) {
      // Keyboard shortcut for key '6'
      this.setState({ visibleModal: null })
      this.props.navigate(`/search/${l('ROUTE_ORGS')}`)
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.escFunction, false)
  }

  componentDidMount() {
    document.addEventListener('keydown', this.escFunction, false)

    const { autologin, visibleModal } = qs.parse(location.search.replace('?', ''))

    if (visibleModal) {
      this.setState({ visibleModal })
    }

    if (!isLoggedIn() && autologin) {
      this.props.attemptAutoLogin({ signInToken: autologin })
    }

    if (isLoggedIn()) {
      this.props.fetchMyRoles()
    }
  }

  render() {
    const { discoverDropdownVisible, pricingDropdownVisible } = this.state
    const {
      children,
      gsIsDownForMaintenance,
      ogTitle,
      bgColor,
      ogImage,
      ogDescription,
      shareModalHostName,
      shareModalOppTitle,
      shareModalDescription,
      oppStatus,
      fetchPlaces,
      places = [],
      logout,
      locationIsLoading,
      extraInfoModalOpts = {},
      signupSuccessModalOpts = {},
      includePayfastScript,
      userData,
      roleData,
      campaignApplicationOpts = {},
      anythingIsLoading,
      anythingIsSubmitting,
      pageType
    } = this.props

    const modalIsVisible = this.state.visibleModal || this.props.visibleModal
    const loggedIn = isLoggedIn()
    const title = getTitle(ogTitle)
    const image = ogImage || window.location.origin + '/images/giving-tuesday.jpg'
    const description = ogDescription
      ? `${ogDescription.substring(0, 80)} ...`
      : 'Meaningful campaigns, relevant opportunities, more impact.'

    if (isGs && gsIsDownForMaintenance) {
      return <GrowsmartDowntimeMessage />
    }

    const isHomepage = pageType === 'homePage'
    const navbarLoading = isHomepage ? false : anythingIsLoading || anythingIsSubmitting

    return (
      <div style={{ position: 'relative' }}>
        <Wrapper id="pageWrapper" visibleModal={modalIsVisible}>
          <Seo title={title} description={description} image={image} url={window.location} />
          <PayfastPaymentScript includePayfastScript={includePayfastScript} />

          <ConditionalDisplay displayWhen={[isGs]}>
            <Header
              isLoading={navbarLoading}
              loggedIn={loggedIn}
              logout={logout}
              pageType={pageType}
              roleData={roleData}
              toggleMenuModal={this.toggleMenuModal}
              userData={userData}
            />
          </ConditionalDisplay>

          <ConditionalDisplay displayWhen={[isBp]}>
            <Navbar
              isLoading={navbarLoading}
              pageType={pageType}
              discoverDropdownVisible={discoverDropdownVisible}
              pricingDropdownVisible={pricingDropdownVisible}
              toggleMenuModal={this.toggleMenuModal}
              toggleDiscoverDropdown={this.toggleDiscoverDropdown}
              togglePricingDropdown={this.togglePricingDropdown}
            />
          </ConditionalDisplay>

          <Main bgColor={bgColor} visibleModal={modalIsVisible}>
            {children}
          </Main>

          <Footer />
        </Wrapper>

        <ModalsCanvas
          campaignApplicationOpts={campaignApplicationOpts}
          closeAllModals={this.closeAllModals}
          extraInfoModalOpts={extraInfoModalOpts}
          fetchPlaces={fetchPlaces}
          locationIsLoading={locationIsLoading}
          loggedIn={loggedIn}
          oppStatus={oppStatus}
          places={places}
          shareModalOppTitle={shareModalOppTitle}
          shareModalDescription={shareModalDescription}
          shareModalHostName={shareModalHostName}
          signupSuccessModalOpts={signupSuccessModalOpts}
          userData={userData}
          visibleModal={modalIsVisible}
        />
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  fetchPlaces: (input, term) => dispatch(placesModule.fetchPlaces(input, term)),
  attemptAutoLogin: key => dispatch(authModule.attemptAutoLogin(key)),
  fetchMyRoles: () => dispatch(authModule.fetchMyRoles()),
  logout: () => dispatch(authModule.logout())
})

const mapStateToProps = state => {
  // const roleData = state.authentication.roleData || []
  // const hasGodAdminRole = roleData.some(r => r.role_type === constants.ROLES.GOD_MODE)
  // const gsIsDownForMaintenance = !hasGodAdminRole

  return {
    gsIsDownForMaintenance: false,
    places: state.places.data.map(d => d.description),
    userData: state.authentication.userData,
    locationIsLoading: state.places.isLoading,
    anythingIsLoading:
      state.authentication.isLoading ||
      state.applications.isLoading ||
      state.campaigns.isLoading ||
      state.opportunities.isLoading ||
      state.organisations.isLoading ||
      state.people.isLoading,
    anythingIsSubmitting:
      state.authentication.isSubmitting ||
      state.applications.isSubmitting ||
      state.campaigns.isSubmitting ||
      state.opportunities.isSubmitting ||
      state.organisations.isSubmitting ||
      state.people.isSubmitting
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withNavigation(PageWrapper))
